import { useCallback, useEffect, useState } from 'react'
import { WhiteBox } from '../../../../../../../../baseComponents/AppLayout/WhiteBox'
import { ButtonOrLoader } from '../../../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { BigTitleAndRightLayout } from '../../../../../../../../baseComponents/Layout/TitleAndRightLayout'
import PermissionChecker from '../../../../../../../../baseComponents/PermissionChecker'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { useParams } from 'react-router-dom'
import HeaderSingleConfigShippingService from './HeaderSingleCShippingService'
import { ShippingCompanyPayload } from '../../../../../../../../utils/reducers/company/shippingCompanies'
import { ConnectedProps, connect } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import { GroupVatPayload } from '../../../../../../../../utils/reducers/globals/groupVats'
import './singleConfigShippingService.sass'
import LoadingSpinner from '../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { ShippingServiceModelType } from '@teinor/erp/types/company/config/shipping/shippingCompany/shippingService'
import { ShippingZoneGroupModelType } from '@teinor/erp/types/company/config/shipping/shippingZoneGroup'
import SwitchTableShippingRateAndCost from './TableShpRateAndCost'
import { RangeTableDataDict } from '../../../../../../../../baseComponents/RangeTable'
import { joinShippingZonesAndRatesDict, parseDictShippingRatesBeforeSubmit } from '../functions'
import { changeSuccessMessage } from '../../../../../../../../utils/reducers/reduxDispatch'
import useIsLoading from '../../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import TwinForm from '../../../../../../../../forms/TwinForm'
import { BeforeSubmitHandler } from '../../../../../../../../forms/TwinForm/types'
import { ConfigShippingZoneGroupTy } from '../types'

export type ShippingServiceExtended = ShippingServiceModelType & {
    ShippingRate: ShippingZoneGroupModelType[]
    dictShippingRates: RangeTableDataDict
    ShippingRateCost: ShippingZoneGroupModelType[]
    dictShippingRateCosts: RangeTableDataDict
}
type SingleConfigShippingServiceProps = ComponentWithPermissions & ReduxSingleConfigShippingService & {}

const SingleConfigShippingService: React.FC<SingleConfigShippingServiceProps> = ({ userPermissions, shippingCompanies, groupVats }) => {
    const {t} = useTwinTranslation()
    const { dataService, setDataService, prepareUpdateShippingService, handleChangeHeader, loading, getAllData } = useSingleConfigShippingServiceLogic()
    if(!dataService){
        return <LoadingSpinner />
    }
    const title = shippingCompanies?.[dataService?.ShippingCompanyId]?.name + ' - ' + dataService?.name
    return (
        <TwinForm beforeSubmitHandler={prepareUpdateShippingService} className='flex-auto flex'>
            <BigTitleAndRightLayout title={title} className='w-1' RightHeader={
                <PermissionChecker userPermissions={userPermissions} permission='update'>
                    <ButtonOrLoader textButton={t('save', 'Guardar')} buttonIsDisabled={loading}/>
                </PermissionChecker>}>
                <WhiteBox className='flex flex-col flex-auto px-40 py-50'>
                    <HeaderSingleConfigShippingService {...{ userPermissions, dataService, groupVats, handleChangeHeader }} />
                    <SwitchTableShippingRateAndCost  {...{ setDataService, dataService, getAllData }} />
                </WhiteBox>
            </BigTitleAndRightLayout>
        </TwinForm>
    )
}

const useSingleConfigShippingServiceLogic = () => {
    const [dataService, setDataService] = useState<null | ShippingServiceExtended>(null)
    const [shippingZoneGroups, setShippingZoneGroups] = useState<ConfigShippingZoneGroupTy>({})
    const { ShippingServiceId } = useParams()
    const { startLoading, loading, endLoading } = useIsLoading()
    const { t } = useTwinTranslation()
    const successMessage = t('updatedCarrierService', 'Servicio de transporte actualizado')

    const handleChangeHeader = useCallback((field: string, val: string) => {
        setDataService((old)=>{
            if(old){
                return { ...old, [field]: val}
            }
            return null
        })
    }, [setDataService])

    const getShippingZoneGroups = useCallback(async() => {
        const result = await twinFetchPostJSON('/api/app/config/shipping/shippingZone/getAllShippingZoneGroups', {})
        if (result) {
            const shippingZoneGroupsDict = dictionaryComplexFromJsonArr(result)
            setShippingZoneGroups(shippingZoneGroupsDict)
            return shippingZoneGroupsDict
        }
    }, [setShippingZoneGroups])

    const getDataService = useCallback(async (myShippingZoneGroups: ConfigShippingZoneGroupTy) => {
        const result = await twinFetchPostJSON('/api/app/config/shipping/shippingCompany/service/getShippingServiceComplete', { id: ShippingServiceId})
        if (result) {
            result['dictShippingRates'] = joinShippingZonesAndRatesDict(result?.ShippingRates, myShippingZoneGroups )
            result['dictShippingRateCosts'] = joinShippingZonesAndRatesDict(result?.ShippingRateCosts, myShippingZoneGroups )
            setDataService(result)
        }
    }, [ShippingServiceId, setDataService])
    
    const getAllData = useCallback(async () => {
        const myShippingZoneGroups = await getShippingZoneGroups()
        if (myShippingZoneGroups) {
            await getDataService(myShippingZoneGroups)
        }
    }, [getShippingZoneGroups, getDataService])

    const updateShippingService = useCallback(async (vals: TwinDictionary) => {
        if(dataService){
            const dictShippingRates = parseDictShippingRatesBeforeSubmit(dataService.dictShippingRates, dataService.id)
            const dictShippingRateCosts = parseDictShippingRatesBeforeSubmit(dataService.dictShippingRateCosts, dataService.id)
            const { ranges, rangesCost } = dataService
            startLoading()
            const response =  await twinFetchPostJSON('/api/app/config/shipping/shippingCompany/service/updateShippingService', { ...vals, ranges, rangesCost, dictShippingRates, dictShippingRateCosts })
            endLoading()
            if (response) {
                changeSuccessMessage(successMessage)
            }
        }
    }, [dataService, startLoading, endLoading, successMessage ])

    const prepareUpdateShippingService: BeforeSubmitHandler = useCallback((vals) => {
        if(vals['maxPrice'] === '0'){
            delete vals['maxPrice']
        }
        updateShippingService(vals)
        return false
    }, [updateShippingService])

    useEffect(() => {
        getAllData()
    }, [getAllData])


    return { dataService, prepareUpdateShippingService, setDataService, handleChangeHeader, loading, shippingZoneGroups, getAllData }
}

const singleConfigShippingServiceDispatch = {
    setShippingCompanies: (payload: ShippingCompanyPayload) => ({ type: 'CHANGE_SHIPPINGCOMPANY', payload }),
    setGroupVats: (payload: GroupVatPayload) => ({ type: 'CHANGE_GROUPVAT', payload }),
}

export type ReduxSingleConfigShippingService = ConnectedProps<typeof singleConfigShippingServiceConnect>
const mapSingleConfigShippingServiceConnector = (state: AllReduxPayloads) => ({ shippingCompanies: state.shippingCompanies, groupVats: state.groupVats })
const singleConfigShippingServiceConnect = connect(mapSingleConfigShippingServiceConnector, singleConfigShippingServiceDispatch)

const singleConfigShippingServiceConnectLoading = withLoading(SingleConfigShippingService, [{ fetchUrl: '/api/app/config/shipping/shippingCompany/getAllShippingCompanies', propName: 'shippingCompanies', setFunctionName: 'setShippingCompanies'}, { fetchUrl: '/api/app/config/vat/getAllGroupsVat', propName: 'groupVats', setFunctionName: 'setGroupVats' }])

const SingleConfigShippingServiceConnect = singleConfigShippingServiceConnect(singleConfigShippingServiceConnectLoading)


export default SingleConfigShippingServiceConnect